import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import background from '../assets/background1.jpeg';

interface EuroProps {
  lang: string;
}

const CopyButton: React.FC<{ text: string }> = ({ text }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text);
    } else {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className="text-xs bg-[#2d5a2d]/10 hover:bg-[#2d5a2d]/20 text-[#2d5a2d] px-2 py-1 rounded transition-colors duration-200 ml-2 whitespace-nowrap"
    >
      {copied ? t('euro_copied') : t('euro_copy')}
    </button>
  );
};

const BankField: React.FC<{ label: string; value: string; subValue?: string; copyable?: boolean; copyText?: string }> = ({ label, value, subValue, copyable = false, copyText }) => (
  <div className="py-4 border-b border-[#2d5a2d]/10 last:border-b-0">
    <p className="text-sm text-[#2d5a2d]/60 font-medium mb-1">{label}</p>
    <div className="flex items-center justify-between gap-2">
      <p className="text-base text-[#2d5a2d] font-semibold tracking-wide">{value}</p>
      {copyable && <CopyButton text={copyText ?? value.replace(/\s/g, '')} />}
    </div>
    {subValue && (
      <p className="text-base text-[#2d5a2d] font-semibold tracking-wide mt-0.5">{subValue}</p>
    )}
  </div>
);

const Euro: React.FC<EuroProps> = ({ lang }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const langPrefix = lang === 'it' ? '/it' : '/eng';
  const homeLink = `${langPrefix}${location.search}`;

  useEffect(() => {
    i18n.changeLanguage(lang);
  }, [lang, i18n]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div
      className="min-h-screen"
      style={{
        backgroundImage: `url(${background})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
      }}
    >
      <div className="bg-[#2d5a2d] py-6 shadow-md">
        <Link to={homeLink} className="block">
          <h1 className="font-cursive text-3xl text-[#f5f0e6] text-center tracking-wide cursor-pointer">
            Sara & Gavriel
          </h1>
        </Link>
      </div>

      <div className="flex items-start justify-center px-4 py-12">
        <div className="w-full max-w-md bg-white/80 backdrop-blur-sm rounded-xl shadow-lg p-8 text-left">
          <div className="divide-y divide-[#2d5a2d]/10">
            <BankField label={t('euro_beneficiary_label')} value={t('euro_beneficiary')} copyable />
            <BankField label={t('euro_iban_label')} value={t('euro_iban')} copyable />
            <BankField label={t('euro_bic_label')} value={t('euro_bic')} copyable />
            <BankField
              label={t('euro_bank_label')}
              value={t('euro_bank_name')}
              subValue={t('euro_bank_address')}
              copyable
              copyText={`${t('euro_bank_name')}, ${t('euro_bank_address')}`}
            />
            <BankField label={t('euro_correspondent_label')} value={t('euro_correspondent')} copyable />
          </div>

          <p className="font-cursive text-xl text-[#2d5a2d] text-center mt-8">{t('thank_you')}</p>
        </div>
      </div>
    </div>
  );
};

export default Euro;
